import React, { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { Box, Checkbox, Divider, FormControlLabel, FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { departmentsTemplate } from 'apollo/templates/organization';
import PageTitle from 'components/shared/page-title/page-title.component';
import IncomingSection from 'components/lab-workflow/workflow-overview/incoming-section.component';
import ProductionSection from 'components/lab-workflow/workflow-overview/production-section.component';
import AdministrationSection from 'components/lab-workflow/workflow-overview/administration-section.component';
import { Department } from 'models/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

const STORAGE_KEY = 'workflow-overview-visibility';

type VisibilityState = {
    incoming: boolean;
    production: boolean;
    administration: boolean;
    hiddenDepartments: string[];
};

const defaultVisibility: VisibilityState = {
    incoming: true,
    production: true,
    administration: true,
    hiddenDepartments: [],
};

const loadVisibility = (): VisibilityState => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return { ...defaultVisibility, ...JSON.parse(stored) };
    } catch {
        // ignore
    }
    return defaultVisibility;
};

const WorkflowOverview: FC = () => {
    const { t: translate } = useTranslation(['workflow']);
    const [visibility, setVisibility] = useState<VisibilityState>(loadVisibility);
    const currentUser = currentUserVar();
    const isExternalTechnician = permissions.isExternalTechnician(currentUser.role);

    const { data: departmentsData, loading: departmentsLoading } = useQuery(departmentsTemplate, {
        fetchPolicy: 'network-only',
        skip: isExternalTechnician,
    });
    const departments: Department[] = departmentsData?.departments || [];

    const hiddenDepartments = useMemo(() => new Set(visibility.hiddenDepartments), [visibility.hiddenDepartments]);

    const updateVisibility = useCallback((update: Partial<VisibilityState>) => {
        setVisibility((prev) => {
            const next = { ...prev, ...update };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    const toggleDepartment = useCallback((deptName: string) => {
        setVisibility((prev) => {
            const set = new Set(prev.hiddenDepartments);
            if (set.has(deptName)) {
                set.delete(deptName);
            } else {
                set.add(deptName);
            }
            const next = { ...prev, hiddenDepartments: Array.from(set) };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    return (
        <Fragment>
            <Box display="flex" alignItems="center" mb={3}>
                <PageTitle headingStyle="h1" title={translate('WORKFLOW')} disableGutter />
                <FormGroup row sx={{ ml: 'auto' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={visibility.incoming}
                                onChange={(e) => updateVisibility({ incoming: e.target.checked })}
                                size="small"
                            />
                        }
                        label={translate('INCOMING')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={visibility.production}
                                onChange={(e) => updateVisibility({ production: e.target.checked })}
                                size="small"
                            />
                        }
                        label={translate('PRODUCTION')}
                    />
                    {!isExternalTechnician &&
                        departments.map((dept) => (
                            <FormControlLabel
                                key={dept._id}
                                control={
                                    <Checkbox
                                        checked={!hiddenDepartments.has(dept.name)}
                                        onChange={() => toggleDepartment(dept.name)}
                                        disabled={!visibility.production}
                                        size="small"
                                    />
                                }
                                label={dept.name}
                            />
                        ))}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={visibility.administration}
                                onChange={(e) => updateVisibility({ administration: e.target.checked })}
                                size="small"
                            />
                        }
                        label={translate('ADMINISTRATION')}
                    />
                </FormGroup>
            </Box>

            {visibility.incoming && (
                <>
                    <IncomingSection />
                    <Box mb={4}>
                        <Divider />
                    </Box>
                </>
            )}

            {visibility.production && (
                <>
                    <ProductionSection
                        departments={departments}
                        departmentsLoading={departmentsLoading}
                        hiddenDepartments={hiddenDepartments}
                    />
                    <Box mb={4}>
                        <Divider />
                    </Box>
                </>
            )}

            {visibility.administration && <AdministrationSection />}
        </Fragment>
    );
};

export default WorkflowOverview;
